import React, { FormEvent, useEffect, useState } from 'react'
import styled from 'styled-components'
import * as yup from 'yup'
import cn from 'classnames'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { Button } from 'primereact/button'

import Text from '../../../ui/Text'
import { FormHeader } from '../../../lib/form-header'
import { InputGroup, SInput } from '../../../lib/input-group'
import { SContentWrapper, SNotification, SSpacer } from '../../../lib/landing-page-layout'
import { getPortalState } from '../../../lib/portalDomain'
import { PasswordInput } from '../../../lib/password-input'
import storage from '../../../common/utils/storage'
import { useAuthVerifyEmailMutation } from '../../../common/redux/api/web-api'
import useText from '../../hooks/useText'
import { useLocalizationContext } from '../localization-provider'

import { usePortalLoginFlow } from './usePortalLoginFlow'

const SRow = styled.div`
    display: flex;
    justify-content: space-between;
`

const SSignUpButton = styled.div`
    &:hover {
        cursor: pointer;
    }
`

const SForgotPassword = styled.div`
    margin-left: auto;
`

const SForgotPasswordLink = styled.span`
    color: #000;
    cursor: pointer;
    font-weight: var(--font-weight-extra-light);
`

export function formatValidationErrorData<T>(errors: yup.ValidationError, initialErrorData: T): T {
    let errorData = initialErrorData
    for (const err of errors.inner) {
        if (err.path) {
            errorData = {
                ...errorData,
                [err.path]: err.message,
            }
        }
    }

    return errorData
}

const initData = {
    email: '',
    password: '',
}

export type INotification = { text: string; type?: string } | null

type Credentials = {
    email: string
    password: string
}

export function SignInForm() {
    const text = useText()
    const portal = getPortalState(window.location.pathname)
    const routerLocation = useLocation()
    const { locale, setLocale } = useLocalizationContext()
    const from = routerLocation?.state?.from || '/'
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const [submitError, setSubmitError] = useState('')
    const [showPassword, toggleShowPassword] = useState(false)
    const [formData, setFormData] = useState<Credentials>(() => initData)
    const [formErrors, setFormErrors] = useState(() => initData)
    const [notification, setNotification] = useState(storage.getItem<INotification>('notification'))

    useEffect(() => {
        storage.removeItem('notification')
    }, [])

    const credentialsSchema = yup.object().shape({
        email: yup.string().email(text('msgEmailFormatIsIncorrect')).required(text('msgEmailIsRequired')),
        password: yup.string().required(text('msgPasswordRequiredsignin')),
    })

    const showForgotPassword = !portal.isSupportDomain

    const [verifyEmail] = useAuthVerifyEmailMutation()

    useEffect(() => {
        const token = searchParams?.get('token')
        if (token) {
            verifyEmail({ token })
                .unwrap()
                .then(() => {
                    setNotification({ text: 'msgEmailVerified', type: 'notice' })
                })
                .catch(() => {
                    setNotification({ text: 'msgEmailVerifiedFailed', type: 'alert' })
                })
        }
    }, [searchParams, verifyEmail])

    const { loading, submitLogin } = usePortalLoginFlow({
        from,
        locale,
        setLocale,
        setNotification,
        navigate,
    })

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()
        const fields = new FormData(event.currentTarget)
        const credentials: Credentials = {
            email: String(fields.get('email') ?? ''),
            password: String(fields.get('password') ?? ''),
        }
        setFormData(credentials)
        setNotification(null)
        setSubmitError('')

        credentialsSchema
            .validate(credentials, { abortEarly: false })
            .then(async () => {
                setFormErrors(() => initData)
                const errorMessage = await submitLogin(credentials)
                if (errorMessage) {
                    setSubmitError(errorMessage)
                }
            })
            .catch((error) => {
                setFormErrors(() => formatValidationErrorData<Credentials>(error, initData) || initData)
            })
    }

    const changeFormData = (value: string, parameterName: string) => {
        setFormData((prev) => ({
            ...prev,
            [parameterName]: value,
        }))
        setFormErrors((prev) => ({
            ...prev,
            [parameterName]: '',
        }))
    }

    return (
        <SContentWrapper>
            {submitError && (
                <SSpacer>
                    <SNotification data-testid="signInError" error={submitError !== ''}>
                        {submitError}
                    </SNotification>
                </SSpacer>
            )}

            {notification && (
                <SSpacer>
                    <SNotification error={notification?.type === 'alert'} data-testid="notification">
                        {text(notification.text)}
                    </SNotification>
                </SSpacer>
            )}

            <SSpacer>
                <FormHeader inverted={portal.isSupportDomain} data-testid="header" />
            </SSpacer>

            <form onSubmit={handleSubmit} noValidate>
                <SSpacer>
                    <InputGroup
                        label={formData.email.length > 0 ? text('lblEmail') : ''}
                        errorTestId="signInEmailErrorMsg"
                        errorMsg={formErrors.email}
                        inverted={portal.isSupportDomain}
                    >
                        <SInput
                            inverted={portal.isSupportDomain}
                            data-testid="emailInput"
                            type="email"
                            name="email"
                            autoComplete="username"
                            placeholder={text('lblEmail')}
                            onChange={(event) => changeFormData(event.currentTarget.value, 'email')}
                            autoFocus
                            formNoValidate
                        />
                    </InputGroup>
                </SSpacer>

                <SSpacer>
                    <PasswordInput
                        name="password"
                        autoComplete="current-password"
                        label={formData.password.length > 0 ? text('lblPassword') : ''}
                        placeholder={text('lblPassword')}
                        onChange={(value: string) => changeFormData(value, 'password')}
                        showPassword={showPassword}
                        error={formErrors.password}
                        errorTestId="signInPasswordErrorMsg"
                        toggleShowPasswordFlag={toggleShowPassword}
                        hasText={formData.password.length > 0}
                    />
                </SSpacer>

                <SSpacer>
                    <Button
                        loading={loading}
                        label={text('lblSignIn')}
                        className={cn('sign-in-btn', 'w-full')}
                        data-testid="submitButton"
                        type="submit"
                        icon="pi pi-arrow-right pi-icon-green"
                        iconPos="right"
                    />
                </SSpacer>
                <SRow>
                    {(portal.isUserappDomain || portal.isPartnerDomain) && (
                        <SSignUpButton
                            onClick={() =>
                                navigate(portal.isPartnerDomain ? '/register-partner' : '/register-user', {
                                    replace: true,
                                })
                            }
                            data-testid="signUpBtn"
                        >
                            <Text code="lblSignUp" />
                        </SSignUpButton>
                    )}
                    {showForgotPassword && (
                        <SForgotPassword>
                            <SForgotPasswordLink
                                onClick={() => navigate(`/reset-password-request`, { replace: true })}
                                data-testid="forgotPasswordBtn"
                            >
                                <Text code="lblForgotPassword" />
                            </SForgotPasswordLink>
                        </SForgotPassword>
                    )}
                </SRow>
            </form>
        </SContentWrapper>
    )
}
